import {
    Dispatch,
    ReactNode,
    SetStateAction,
    createContext,
    useCallback,
    useContext,
    useEffect,
    useRef,
    useState,
} from 'react'
import { useParams } from 'react-router-dom'
import * as Accordion from '@radix-ui/react-accordion'
import { DateTime } from 'luxon'
import styled from 'styled-components'
import Spinner from '../components/atoms/Spinner'
import useOverviewLists from '../components/overview/useOverviewLists'
import { useGetMeetingPreparationTasks } from '../services/api/meeting-preparation-tasks.hooks'
import {
    findFirstNonEmptyOverviewView,
    getActiveMeetingPreparationTasks,
    isOverviewViewNonEmpty,
} from '../utils/overview'
import { emptyFunction } from '../utils/utils'

interface TOverviewContext {
    openListIds: string[]
    setOpenListIds: Dispatch<SetStateAction<string[]>>
    motionlessListIds: string[]
    expandAll: () => void
    collapseAll: () => void
    announcement: string
    announceListOpened: (name: string) => void
    registerTaskExit: (taskId: string, startExit: () => boolean) => () => void
    startTaskExits: (taskIds: string[]) => boolean
}

const OverviewContext = createContext<TOverviewContext>({
    openListIds: [],
    setOpenListIds: emptyFunction,
    motionlessListIds: [],
    expandAll: emptyFunction,
    collapseAll: emptyFunction,
    announcement: '',
    announceListOpened: emptyFunction,
    registerTaskExit: () => emptyFunction,
    startTaskExits: () => false,
})

const rememberedOverviewOpenIds = new Map<string, string[]>()
const localDay = () => DateTime.local().toISODate()

export const getRememberedOverviewOpenListIds = () => {
    const ids = rememberedOverviewOpenIds.get(localDay())
    return ids ? [...ids] : undefined
}

const rememberOverviewOpenListIds = (ids: string[]) => rememberedOverviewOpenIds.set(localDay(), [...ids])

// The root is layout-neutral so existing card spacing and list-pane sizing stay unchanged.
const AccordionRoot = styled(Accordion.Root)`
    display: contents;
`
interface OverviewAccordionContextProps {
    children: ReactNode
    listIds: string[]
    initialOpenId?: string
    initialOpenIds?: string[]
    rememberOpenIds?: boolean
}

const captureAccordionMotionStart = (currentIds: string[], nextIds: string[]) => {
    if (typeof document === 'undefined') return
    const changedIds = new Set([
        ...currentIds.filter((id) => !nextIds.includes(id)),
        ...nextIds.filter((id) => !currentIds.includes(id)),
    ])
    if (changedIds.size === 0) return

    const motionStarts = Array.from(
        document.querySelectorAll<HTMLElement>('[data-overview-accordion-content]')
    ).flatMap((content) => {
        const id = content.dataset.overviewAccordionContent
        if (!id || !changedIds.has(id)) return []
        return [
            {
                content,
                height: content.getBoundingClientRect().height,
                opacity: window.getComputedStyle(content).opacity,
            },
        ]
    })

    motionStarts.forEach(({ content, height, opacity }) => {
        content.style.setProperty('--overview-accordion-start-height', `${height}px`)
        content.style.setProperty('--overview-accordion-start-opacity', opacity)
    })
}

export const OverviewAccordionContextProvider = ({
    children,
    listIds,
    initialOpenId,
    initialOpenIds = [],
    rememberOpenIds = false,
}: OverviewAccordionContextProps) => {
    // A seed is the page-load rule, not a user choice. Remember only changes made after mount so an
    // untouched seed that empties elsewhere is recalculated when the user returns to Overview.
    const [openListIds, setOpenListIdsState] = useState<string[]>(initialOpenIds)
    const [motionlessListIds, setMotionlessListIds] = useState<string[]>(initialOpenIds)
    const [announcement, setAnnouncement] = useState('')
    const openListIdsRef = useRef(openListIds)
    const taskExitsRef = useRef(new Map<string, Set<() => boolean>>())
    const setOpenListIds = useCallback<Dispatch<SetStateAction<string[]>>>(
        (update) => {
            const currentIds = openListIdsRef.current
            const nextIds = typeof update === 'function' ? update(currentIds) : update
            if (nextIds === currentIds) return
            const changedIds = new Set([
                ...currentIds.filter((id) => !nextIds.includes(id)),
                ...nextIds.filter((id) => !currentIds.includes(id)),
            ])
            captureAccordionMotionStart(currentIds, nextIds)
            if (changedIds.size > 0) setMotionlessListIds((ids) => ids.filter((id) => !changedIds.has(id)))
            openListIdsRef.current = nextIds
            setOpenListIdsState(nextIds)
            if (rememberOpenIds) rememberOverviewOpenListIds(nextIds)
        },
        [rememberOpenIds]
    )
    const expandAll = () => setOpenListIds(listIds)
    const collapseAll = () => setOpenListIds([])
    const announceListOpened = useCallback((name: string) => setAnnouncement(`${name} opened`), [])
    const registerTaskExit = useCallback((taskId: string, startExit: () => boolean) => {
        const taskExits = taskExitsRef.current
        const exitsForTask = taskExits.get(taskId) ?? new Set()
        exitsForTask.add(startExit)
        taskExits.set(taskId, exitsForTask)

        return () => {
            exitsForTask.delete(startExit)
            if (exitsForTask.size === 0) taskExits.delete(taskId)
        }
    }, [])
    const startTaskExits = useCallback((taskIds: string[]) => {
        let shouldWait = false
        taskIds.forEach((taskId) =>
            taskExitsRef.current.get(taskId)?.forEach((startExit) => {
                shouldWait = startExit() || shouldWait
            })
        )
        return shouldWait
    }, [])

    useEffect(() => {
        if (initialOpenId) setOpenListIds((ids) => (ids.includes(initialOpenId) ? ids : [...ids, initialOpenId]))
    }, [initialOpenId])

    useEffect(() => {
        if (!announcement) return
        const timeout = setTimeout(() => setAnnouncement(''), 4000)
        return () => clearTimeout(timeout)
    }, [announcement])

    return (
        <OverviewContext.Provider
            value={{
                openListIds,
                setOpenListIds,
                motionlessListIds,
                expandAll,
                collapseAll,
                announcement,
                announceListOpened,
                registerTaskExit,
                startTaskExits,
            }}
        >
            {children}
        </OverviewContext.Provider>
    )
}

export const OverviewContextProvider = ({ children }: { children: ReactNode }) => {
    const { lists, isLoading } = useOverviewLists()
    const { overviewViewId, overviewItemId } = useParams()
    const { data: meetingPreparationTasks, isLoading: isMeetingPreparationTasksLoading } =
        useGetMeetingPreparationTasks()

    if (isLoading || isMeetingPreparationTasksLoading) return <Spinner />

    const activeMeetingPreparationCount = getActiveMeetingPreparationTasks(meetingPreparationTasks).length
    const deepLinkedView =
        overviewViewId && overviewItemId ? lists.find((list) => list.id === overviewViewId) : undefined
    const rememberedIds = deepLinkedView ? undefined : getRememberedOverviewOpenListIds()
    const rememberedViews = rememberedIds
        ? lists.filter(
              (list) => rememberedIds.includes(list.id) && isOverviewViewNonEmpty(list, activeMeetingPreparationCount)
          )
        : []
    const firstNonEmptyView = findFirstNonEmptyOverviewView(lists, activeMeetingPreparationCount)
    const initialOpenIds = deepLinkedView
        ? [deepLinkedView.id]
        : rememberedIds !== undefined
        ? rememberedViews.map((view) => view.id)
        : firstNonEmptyView
        ? [firstNonEmptyView.id]
        : []

    return (
        <OverviewAccordionContextProvider
            listIds={lists.map((list) => list.id)}
            initialOpenIds={initialOpenIds}
            rememberOpenIds
        >
            {children}
        </OverviewAccordionContextProvider>
    )
}

const useOverviewContext = () => useContext(OverviewContext)

export const OverviewAccordionRoot = ({ children }: { children: ReactNode }) => {
    const { openListIds, setOpenListIds } = useOverviewContext()
    return (
        <AccordionRoot type="multiple" value={openListIds} onValueChange={setOpenListIds}>
            {children}
        </AccordionRoot>
    )
}

export default useOverviewContext
